import { Space } from 'antd'
import { ConfigContext, defaultPrefixCls } from 'antd/es/config-provider'
import { FormContext } from 'antd/es/form/context'
import { useWatch } from 'antd/es/form/Form'
import useFormInstance from 'antd/es/form/hooks/useFormInstance'
import Input, { InputProps } from 'antd/es/input'
import Select from 'antd/es/select'
import { AsYouType } from 'libphonenumber-js'
import {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  checkValidity,
  cleanInput,
  displayFormat,
  getCountry,
  getDefaultISO2Code,
  getFormattedNumber,
  getMetadata,
  getRawValue,
  parsePhoneNumber,
  usePhone,
} from 'react-phone-hooks'
import { injectStyles, jsonToCss } from 'react-phone-hooks/styles'
import commonStyles from 'react-phone-hooks/stylesheet.json'
import { phoneMetadataToString } from '../../utils/PhoneFormatter'
import customStyles from './stylesheet.json'

const SBPhoneInput = forwardRef(
  (
    {
      value: initialValue = undefined,
      sbInitialValue = undefined,
      country = getDefaultISO2Code(),
      disabled = false,
      enableArrow = false,
      enableSearch = false,
      disableDropdown = false,
      disableParentheses = false,
      onlyCountries = [],
      excludeCountries = [],
      preferredCountries = [],
      searchNotFound: defaultSearchNotFound = 'No country found',
      searchPlaceholder: defaultSearchPlaceholder = 'Search country',
      dropdownRender = (node) => node,
      onMount: handleMount = () => null,
      onInput: handleInput = () => null,
      onChange: handleChange = () => null,
      onKeyDown: handleKeyDown = () => null,
      ...antInputProps
    }: PhoneInputProps,
    forwardedRef: any
  ) => {
    const formInstance = useFormInstance()
    const { locale = {}, getPrefixCls } = useContext(ConfigContext)
    const formContext = useContext(FormContext)
    const inputRef = useRef<any>(null)
    const searchRef = useRef<any>(null)
    const selectedRef = useRef<boolean>(false)
    const initiatedRef = useRef<boolean>(false)
    const [query, setQuery] = useState<string>('')
    const [minWidth, setMinWidth] = useState<number>(0)
    const [countryCode, setCountryCode] = useState<string>(country)

    const {
      locale: localeIdentifier,
      searchNotFound = defaultSearchNotFound,
      searchPlaceholder = defaultSearchPlaceholder,
      countries = new Proxy({}, { get: (_: any, prop: any) => prop }),
    } = (locale as any).PhoneInput || {}

    const prefixCls = getPrefixCls()
    injectMergedStyles(prefixCls)

    const computeInitialSbValue = (initialValue: PhoneInputProps['value']) =>
      !initialValue
        ? '+33'
        : typeof initialValue === 'string'
          ? initialValue
          : phoneMetadataToString(initialValue)

    const [sbValue, setSBValue] = useState(computeInitialSbValue(initialValue))

    useEffect(() => {
      if (!sbInitialValue) return
      setSBValue(sbInitialValue)
    }, [sbInitialValue])

    const { value, pattern, metadata, setValue, countriesList } = usePhone({
      query,
      country,
      countryCode,
      initialValue,
      onlyCountries,
      excludeCountries,
      preferredCountries,
      disableParentheses,
      locale: localeIdentifier,
    })

    const selectValue = useMemo(() => {
      const metadata = getCountry(countryCode as any)

      if (metadata && metadata[0] && metadata[2]) {
        return metadata[0] + metadata[2]
      }
      const metadataFromValue = getMetadata(getRawValue(value), countriesList)
      if (metadataFromValue && metadataFromValue[0] && metadataFromValue[2]) {
        return metadataFromValue[0] + metadataFromValue[2]
      }
      return ''
    }, [countryCode, countriesList, value])

    const namePath = useMemo(() => {
      let path = []
      let formName = (formContext as any)?.name || ''
      let fieldName = (antInputProps as any)?.id || ''
      if (formName) {
        path.push(formName)
        fieldName = fieldName.slice(formName.length + 1)
      }
      return path.concat(fieldName.split('_'))
    }, [antInputProps, formContext])

    const phoneValue = useWatch(namePath, formInstance)

    const setFieldValue = useCallback(
      (value: SBPhoneNumber) => {
        if (formInstance) formInstance.setFieldValue(namePath, value)
      },
      [formInstance, namePath]
    )

    const onChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const formattedNumber = selectedRef.current
          ? event.target.value
          : getFormattedNumber(event.target.value, pattern)
        selectedRef.current = false
        const phoneMetadata = parsePhoneNumber(formattedNumber, countriesList)
        setCountryCode(phoneMetadata.isoCode as any)
        const typed = new AsYouType().input(event.target.value)
        setValue(typed)
        setSBValue(typed)
        setQuery('')
        handleChange(
          {
            ...phoneMetadata,
            typed: typed,
            valid: (strict: boolean) => checkValidity(phoneMetadata, strict),
          },
          event
        )
      },
      [countriesList, handleChange, pattern, setValue]
    )

    const onMount = useCallback(
      (value: SBPhoneNumber) => {
        setFieldValue(value)
        handleMount(value)
      },
      [handleMount, setFieldValue]
    )

    const onDropdownVisibleChange = useCallback(
      (open: boolean) => {
        if (open && enableSearch)
          setTimeout(() => searchRef.current.focus(), 100)
      },
      [enableSearch]
    )

    const ref = useCallback(
      (node: any) => {
        ;[forwardedRef, inputRef].forEach((ref) => {
          if (typeof ref === 'function') ref(node)
          else if (ref != null) ref.current = node
        })
      },
      [forwardedRef]
    )

    const isManualSelectionRef = useRef<boolean>(false)
    const pendingCountryCodeRef = useRef<string | null>(null)

    useEffect(() => {
      const rawValue = getRawValue(phoneValue)
      const metadata = getMetadata(rawValue)

      if (isManualSelectionRef.current && pendingCountryCodeRef.current) {
        if (metadata?.[0] === pendingCountryCodeRef.current) {
          isManualSelectionRef.current = false
          pendingCountryCodeRef.current = null
        }
        return
      }

      // Skip if value has not been updated by `setFieldValue`.
      if (!metadata?.[3] || rawValue === getRawValue(value)) return
      if (metadata?.[0] === countryCode) return

      const formattedNumber = getFormattedNumber(
        rawValue,
        metadata?.[3] as string
      )
      const phoneMetadata = parsePhoneNumber(formattedNumber)
      setFieldValue({
        ...phoneMetadata,
        typed: sbValue,
        valid: (strict: boolean) => checkValidity(phoneMetadata, strict),
      })

      setCountryCode(metadata?.[0] as string)
      setValue(formattedNumber)
    }, [phoneValue, value, setFieldValue, setValue, countryCode, sbValue])

    useEffect(() => {
      if (initiatedRef.current) return
      initiatedRef.current = true
      let initialValue = getRawValue(value)
      if (!initialValue.startsWith(metadata?.[2] as string)) {
        initialValue = metadata?.[2] as string
      }
      const formattedNumber = getFormattedNumber(initialValue, pattern)
      const phoneMetadata = parsePhoneNumber(formattedNumber, countriesList)
      onMount({
        ...phoneMetadata,
        typed: sbValue,
        valid: (strict: boolean) => checkValidity(phoneMetadata, strict),
      })
      setCountryCode(phoneMetadata.isoCode as any)
      setValue(formattedNumber)
    }, [countriesList, metadata, onMount, pattern, setValue, value])

    const suffixIcon = useMemo(() => {
      return (
        enableArrow && (
          <span role="img" className="anticon" style={{ paddingLeft: 8 }}>
            <svg
              className="icon"
              viewBox="0 0 1024 1024"
              focusable="false"
              fill="currentColor"
              width="16"
              height="18"
            >
              <path d="M848 368a48 48 0 0 0-81.312-34.544l-0.016-0.016-254.784 254.784-251.488-251.488a48 48 0 1 0-71.04 64.464l-0.128 0.128 288 288 0.016-0.016a47.84 47.84 0 0 0 34.544 14.688h0.224a47.84 47.84 0 0 0 34.544-14.688l0.016 0.016 288-288-0.016-0.016c8.32-8.624 13.44-20.368 13.44-33.312z" />
            </svg>
          </span>
        )
      )
    }, [enableArrow])

    const countriesSelect = useMemo(
      () => (
        <Select
          style={{ width: 'auto' }}
          suffixIcon={null}
          value={selectValue}
          disabled={disabled}
          open={disableDropdown ? false : undefined}
          onSelect={(selectedOption, { key }) => {
            const [selectedIso, mask] = key.split('_')
            const selectedCountryCode = selectedIso
            const formattedNumber = displayFormat(
              cleanInput(mask, mask).join('')
            )
            const phoneMetadata = parsePhoneNumber(
              formattedNumber,
              countriesList,
              selectedCountryCode
            )

            isManualSelectionRef.current = true
            pendingCountryCodeRef.current = selectedCountryCode

            setFieldValue({
              ...phoneMetadata,
              typed: sbValue,
              valid: (strict: boolean) => checkValidity(phoneMetadata, strict),
            })
            setCountryCode(selectedCountryCode)
            setValue(formattedNumber)
            setQuery('')
            selectedRef.current = true
            const nativeInputValueSetter = (
              Object.getOwnPropertyDescriptor(
                HTMLInputElement.prototype,
                'value'
              ) as any
            ).set
            nativeInputValueSetter.call(inputRef.current.input, formattedNumber)
            inputRef.current.input.dispatchEvent(
              new Event('change', { bubbles: true })
            )
            inputRef.current.input.focus()

            setTimeout(() => {
              if (isManualSelectionRef.current) {
                isManualSelectionRef.current = false
                pendingCountryCodeRef.current = null
              }
            }, 500)
          }}
          labelRender={() => (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <div className={`flag ${countryCode}`} />
              {enableArrow && suffixIcon}
            </div>
          )}
          popupStyle={{ minWidth }}
          onOpenChange={onDropdownVisibleChange}
          getPopupContainer={(triggerNode) =>
            triggerNode.parentElement || document.body
          }
          popupRender={(menu) => (
            <div className={`${prefixCls}-phone-input-search-wrapper`}>
              {enableSearch && (
                <Input
                  value={query}
                  ref={searchRef}
                  placeholder={searchPlaceholder}
                  onInput={({ target }: any) => setQuery(target.value)}
                />
              )}
              {countriesList.length ? (
                menu
              ) : (
                <div className="ant-select-item-empty">{searchNotFound}</div>
              )}
            </div>
          )}
        >
          {countriesList.map(([iso, name, dial, pattern]) => {
            const mask = disableParentheses
              ? pattern.replace(/[()]/g, '')
              : pattern
            return (
              <Select.Option value={iso + dial} key={`${iso}_${mask}`}>
                <div className={`${prefixCls}-phone-input-select-item`}>
                  <div className={`flag ${iso}`} />
                  {countries[name]}&nbsp;{displayFormat(mask)}
                </div>
              </Select.Option>
            )
          })}
        </Select>
      ),
      [
        selectValue,
        suffixIcon,
        countryCode,
        query,
        disabled,
        disableParentheses,
        disableDropdown,
        onDropdownVisibleChange,
        minWidth,
        searchNotFound,
        countries,
        countriesList,
        setFieldValue,
        setValue,
        prefixCls,
        enableSearch,
        searchPlaceholder,
        sbValue,
        enableArrow,
      ]
    )

    return (
      <div
        className={`${prefixCls}-phone-input-wrapper`}
        ref={(node) => setMinWidth(node?.offsetWidth || 0)}
      >
        <Space.Compact block>
          <>{dropdownRender(countriesSelect)}</>
          <Input
            ref={ref}
            inputMode="tel"
            value={sbValue}
            onChange={onChange}
            disabled={disabled}
            {...antInputProps}
          />
        </Space.Compact>
      </div>
    )
  }
)

export default SBPhoneInput

export type SBPhoneNumber = {
  typed?: string
  countryCode?: number | null
  areaCode?: string | null
  phoneNumber?: string | null
  isoCode?: string
  valid?(strict?: boolean): boolean
}

export interface PhoneInputProps extends Omit<
  InputProps,
  'value' | 'onChange'
> {
  value?: SBPhoneNumber | string
  country?: string
  enableArrow?: boolean
  enableSearch?: boolean
  searchNotFound?: string
  searchPlaceholder?: string
  disableDropdown?: boolean
  disableParentheses?: boolean
  onlyCountries?: string[]
  excludeCountries?: string[]
  preferredCountries?: string[]
  dropdownRender?: (menu: ReactNode) => ReactNode
  onMount?(value: SBPhoneNumber): void
  onInput?(event: ChangeEvent<HTMLInputElement>): void
  onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void
  /** NOTE: This differs from the antd Input onChange interface */
  onChange?(value: SBPhoneNumber, event: ChangeEvent<HTMLInputElement>): void
  /** NOTE: Added by SB, to be removed on clean */
  sbInitialValue?: string
}

export const injectMergedStyles = (prefixCls: string = 'ant') => {
  const stylesheet = JSON.parse(JSON.stringify(customStyles))
  const common = JSON.parse(JSON.stringify(commonStyles))
  const processedStyles: { [key: string]: any } = {}
  Object.entries({ ...common, ...stylesheet }).forEach(([key, value]) => {
    if (prefixCls && prefixCls !== defaultPrefixCls) {
      const newKey = key.replace(/\.ant-/g, `.${prefixCls}-`)
      processedStyles[newKey] = value
    } else {
      processedStyles[key] = value
    }
  })

  return injectStyles(jsonToCss(processedStyles))
}
